import { FilterItem, FilterList } from '@/components_v2/Filtersv3/filter-item';
import { FilterCalendar } from '@/components_v2/Filtersv3/reactive-filter-calendar';
import { MacroColumnFilter, MacroTableFilter } from '@/gql/types';
import { useFetchOptions } from '@/hooks/useFetchOptions';
import { Skeleton, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface FileTransferFiltersProps {
  type: 'export' | 'import';
}

export default function FileTransferFilters({
  type,
}: FileTransferFiltersProps) {
  const { t } = useTranslation();
  const isExport = type === 'export';
  const tableFilter = isExport
    ? MacroTableFilter.Exports
    : MacroTableFilter.Imports;

  // State to control lazy loading of filters
  const [loadedFilters, setLoadedFilters] = React.useState({
    users: false,
    templates: false,
    types: false,
    teams: false,
    formularyGroups: false,
  });

  // User filter - lazy loaded
  const usersQuery = useFetchOptions({
    column: MacroColumnFilter.FullName,
    table: MacroTableFilter.Users,
    first: 50,
    filters: {},
    skip: !loadedFilters.users,
  });

  // Template filter (formularies) - lazy loaded
  const templatesQuery = useFetchOptions({
    column: MacroColumnFilter.Name,
    table: MacroTableFilter.Formularies,
    first: 50,
    filters: {},
    skip: !loadedFilters.templates,
  });

  // Export/Import type filter - load immediately (usually small dataset)
  const typeQuery = useFetchOptions({
    column: isExport
      ? MacroColumnFilter.ExportType
      : MacroColumnFilter.ImportType,
    table: tableFilter,
    filters: {},
    skip: !loadedFilters.types,
  });

  // Team filter - lazy loaded
  const teamsQuery = useFetchOptions({
    column: MacroColumnFilter.Name,
    table: MacroTableFilter.Teams,
    first: 50,
    filters: {},
    skip: !loadedFilters.teams,
  });

  // Formulary group filter - lazy loaded
  const formularyGroupsQuery = useFetchOptions({
    column: MacroColumnFilter.Name,
    table: MacroTableFilter.FormularyGroups,
    first: 50,
    filters: {},
    skip: !loadedFilters.formularyGroups,
  });

  // Load filters progressively to avoid blocking the UI
  React.useEffect(() => {
    const loadFiltersSequentially = async () => {
      // Load type filter first (usually smallest)
      setLoadedFilters((prev) => ({ ...prev, types: true }));

      // Wait a bit then load users
      setTimeout(() => {
        setLoadedFilters((prev) => ({ ...prev, users: true }));
      }, 100);

      // Load templates after users
      setTimeout(() => {
        setLoadedFilters((prev) => ({ ...prev, templates: true }));
      }, 200);

      // Load teams
      setTimeout(() => {
        setLoadedFilters((prev) => ({ ...prev, teams: true }));
      }, 300);

      // Load formulary groups last
      setTimeout(() => {
        setLoadedFilters((prev) => ({ ...prev, formularyGroups: true }));
      }, 400);
    };

    loadFiltersSequentially();
  }, []);

  // Search callbacks
  const onSearchUserNameFilter = React.useCallback(
    (search: string) => {
      usersQuery.refetch({
        search,
      });
    },
    [usersQuery],
  );

  const onSearchTemplateNameFilter = React.useCallback(
    (search: string) => {
      templatesQuery.refetch({
        search,
      });
    },
    [templatesQuery],
  );

  const onSearchTeamNameFilter = React.useCallback(
    (search: string) => {
      teamsQuery.refetch({
        search,
      });
    },
    [teamsQuery],
  );

  const onSearchFormularyGroupNameFilter = React.useCallback(
    (search: string) => {
      formularyGroupsQuery.refetch({
        search,
      });
    },
    [formularyGroupsQuery],
  );

  // Fetch more callbacks
  const fetchMoreUserNameFilter = React.useCallback(() => {
    usersQuery.fetchMore({
      variables: {
        after: usersQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [usersQuery]);

  const fetchMoreTemplateNameFilter = React.useCallback(() => {
    templatesQuery.fetchMore({
      variables: {
        after: templatesQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [templatesQuery]);

  const fetchMoreTeamNameFilter = React.useCallback(() => {
    teamsQuery.fetchMore({
      variables: {
        after: teamsQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [teamsQuery]);

  const fetchMoreFormularyGroupNameFilter = React.useCallback(() => {
    formularyGroupsQuery.fetchMore({
      variables: {
        after: formularyGroupsQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [formularyGroupsQuery]);

  // Skeleton component for loading states
  const FilterSkeleton = () => <Skeleton height="40px" borderRadius="md" />;

  return (
    <FilterList>
      {loadedFilters.users ? (
        <FilterItem
          label={t('common.user')}
          filterKey="id.users"
          loading={usersQuery.loading}
          options={usersQuery.options}
          onSearch={onSearchUserNameFilter}
          hasMore={usersQuery.pageInfo?.hasNextPage}
          fetchMore={fetchMoreUserNameFilter}
        />
      ) : (
        <FilterSkeleton />
      )}

      {isExport &&
        (loadedFilters.templates ? (
          <FilterItem
            label={t('common.form_template')}
            filterKey="formularyIds"
            loading={templatesQuery.loading}
            options={templatesQuery.options}
            onSearch={onSearchTemplateNameFilter}
            hasMore={templatesQuery.pageInfo?.hasNextPage}
            fetchMore={fetchMoreTemplateNameFilter}
          />
        ) : (
          <FilterSkeleton />
        ))}

      {loadedFilters.types ? (
        <FilterItem
          label={isExport ? t('common.export_type') : t('common.import_type')}
          filterKey={isExport ? 'exportType' : 'importType'}
          loading={typeQuery.loading}
          options={typeQuery.options}
        />
      ) : (
        <FilterSkeleton />
      )}

      {loadedFilters.teams ? (
        <FilterItem
          label={t('common.user_team')}
          filterKey="id.teams"
          loading={teamsQuery.loading}
          options={teamsQuery.options}
          onSearch={onSearchTeamNameFilter}
          hasMore={teamsQuery.pageInfo?.hasNextPage}
          fetchMore={fetchMoreTeamNameFilter}
        />
      ) : (
        <FilterSkeleton />
      )}

      {isExport &&
        (loadedFilters.formularyGroups ? (
          <FilterItem
            label={t('common.template_grouper')}
            filterKey="formularyGroupIds"
            loading={formularyGroupsQuery.loading}
            options={formularyGroupsQuery.options}
            onSearch={onSearchFormularyGroupNameFilter}
            hasMore={formularyGroupsQuery.pageInfo?.hasNextPage}
            fetchMore={fetchMoreFormularyGroupNameFilter}
          />
        ) : (
          <FilterSkeleton />
        ))}

      <FilterCalendar label={t('common.request_date')} filterKey="created_at" />
    </FilterList>
  );
}
